import { useState } from "react";
import {
  ManifestDetailInterface,
  ManifestDetailSingleInterface,
  ManifestIncidence,
  ManifestShipmentIncidents,
} from "../../interfaces/ManifestInterface";
import { FormTextInput } from "../../components/FormFields";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import LoadingIcon from "../../components/LodingIcon";
import { CurrencyDollarIcon } from "@heroicons/react/24/outline";

interface dispatchTableProps {
  data?: ManifestDetailSingleInterface[];
  showDayCreation?: boolean;
  showColumStatus?: boolean;
  showShipper?: boolean;
  showConsignee?: boolean;
  showPiece?: boolean;
  showDeliveryAddress?: boolean;
  onAdd?: (newRow: ManifestDetailInterface) => void;
  onRowClick?: (shipmentNumber: string) => void;
  ifConsult?: boolean | false;
  showItem?: boolean | true;
  showAllStatus?: boolean | false;
}

export const manifestShipmentIncident = (status: ManifestShipmentIncidents) => {
  switch (status) {
    case ManifestShipmentIncidents.ANULADAS:
      return "Anuladas";
    case ManifestShipmentIncidents.FALTANTES:
      return "Faltantes";
    default:
      return "Desconocida";
  }
};

// const INITIAL_SHIPMENT_INCIDENTS = [
//   ManifestShipmentIncidents.ANULADAS,
//   ManifestShipmentIncidents.FALTANTES,
// ].map((status) => ({
//   name: manifestShipmentIncident(status),
//   value: status,
//   checked: false,
// }));

const ManifestDetailsTable: React.FC<dispatchTableProps> = ({
  data,
  showDayCreation,
  showColumStatus,
  showShipper = false,
  showConsignee = false,
  showDeliveryAddress = false,
  showPiece = false,
  onRowClick,
  ifConsult,
  showItem,
  showAllStatus,
}) => {
  const [filterText, setFilterText] = useState("");

  const filteredData = data?.filter((row) => {
    const statusIsValid = showAllStatus || row.statusID !== 34; // Validación de statusID condicionada por showAllStatus

    return (
      statusIsValid && // Se aplica la validación de statusID (o se omite si showAllStatus es true)
      (row.shipmentNumber.includes(filterText) ||
        row.serviceName?.includes(filterText) ||
        row.locationDestinationCode.includes(filterText))
    );
  });

  // total de guías únicas
  const totalShipmentsUnique = filteredData
    ?.filter((x) => x.statusID !== 38 && x.statusID !== 34) // se excluyen los faltantes=38 y eliminados=34
    .reduce((acumulador, objeto) => {
      acumulador[objeto.shipmentNumber] =
        (acumulador[objeto.shipmentNumber] || 0) + 1;
      return acumulador;
    }, {} as Record<string, number>);

  const totalShipments = Object.keys(totalShipmentsUnique || {}).length;

  // total peso
  const sumTotalPhysicalWeight = filteredData?.reduce(
    (acumulador, objeto) => acumulador + objeto.physicalWeight,
    0
  );

  const sumTotalChargedWeight = filteredData?.reduce(
    (acumulador, objeto) => acumulador + (objeto.chargedWeight ?? 0),
    0
  );

  // total de piezas, se excluyen las piezas con statusID faltantes
  const totalPiecesCount = filteredData?.reduce(
    (sum, row) => sum + (showPiece ? 1 : row.totalPieces),
    0
  );

  // const handleDelete = (id: string) => {
  //   const confirmDelete = window.confirm(
  //     "¿Estás seguro de que deseas eliminar este registro?"
  //   );
  //   if (confirmDelete) {
  //     const updatedData = tableData?.filter(
  //       (row) => row.containerDetailID !== id
  //     );
  //     setTableData(updatedData);
  //   }
  // };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between my-4">
        <div className="mb-4 flex-1">
          <label className="block text-m font-medium leading-6 text-gray-800 mb-2 truncate">
            Filtrar
          </label>
          <FormTextInput
            type="text"
            placeholder="Por número de guía, origen, destino o servicio"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="mb-4 truncate ">
          <label className="block text-m font-medium leading-6 text-gray-800 mb-2 truncate">
            Totales
          </label>
          <div className="border gap-6 bg-white rounded-xl shadow-md hover:shadow-lg flex flex-col justify-between sm:flex-row sm:items-center p-2">
            <div className="flex items-center">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Guías:
              </label>
              <div className="text-gray-700 text-sm font-semibold sm:my-1">
                {totalShipments}
              </div>
            </div>
            <div className="flex items-center">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Piezas:
              </label>
              <div className="text-gray-700 text-sm font-semibold sm:my-1">
                {totalPiecesCount}
              </div>
            </div>
            <div className="flex items-center">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Peso balanza:
              </label>
              <div className="text-gray-700 text-sm font-semibold sm:my-1">
                {sumTotalPhysicalWeight?.toFixed(2)} kg
              </div>
            </div>
            <div className="flex items-center">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Peso facturado:
              </label>
              <div className="text-gray-700 text-sm font-semibold sm:my-1">
                {sumTotalChargedWeight?.toFixed(2)} kg
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="table-auto w-full">
          <thead>
            <tr className="text-center">
              {/* Item,Guía,Origen,Servicio,Pieza,Peso,Destino,Dias Activas,Estatus */}
              {showItem && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  ITEM
                </th>
              )}

              <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                GUÍA
              </th>

              <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                ORIGEN
              </th>

              <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                DESTINO
              </th>

              <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                SERVICIO / MODALIDAD DE PAGO
              </th>

              {showPiece && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  PIEZA
                </th>
              )}

              {!showPiece && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  TOTAL PIEZAS
                </th>
              )}

              <th
                className="px-4 py-2 font-semibold text-xs text-gray-800"
                title="Los totales de peso presentados corresponden a lo que se incluyó en el manifiesto"
                data-te-toggle="tooltip"
              >
                PESO
              </th>

              {showDayCreation && (
                <th className="px-4 py-2 font-semibold text-xs truncate text-gray-800">
                  DÍAS CREACIÓN
                </th>
              )}

              {showShipper && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  REMITENTE
                </th>
              )}

              {showConsignee && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  DESTINATARIO
                </th>
              )}

              {showDeliveryAddress && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  ENTREGA
                </th>
              )}

              {showColumStatus && (
                <th className="px-4 py-2 font-semibold text-xs text-gray-800">
                  ESTATUS
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((row, index) => (
              // <tr
              //   className={`cursor-pointer hover:bg-gray-100 text-center transition-opacity duration-500 ${
              //     index % 2 === 0 ? "bg-gray-50" : "bg-white"
              //   }`}
              <tr // si quieren resaltar los sobrantes solo falta agregar validacion statusID = 37
                className={`cursor-pointer hover:bg-gray-100 text-center transition-opacity duration-500 ${
                  row.statusID === 34 // eliminado
                    ? "bg-red-200"
                    : row.statusID === 38 // faltante
                    ? "bg-orange-200"
                    : index % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white"
                }`}
                key={index}
                title={[row.errors, row.incidenceWarning ? row.warnings : ""]
                  .filter((message) => message)
                  .join(" | ")}
                onClick={() => onRowClick && onRowClick(row.shipmentNumber)}
              >
                {showItem && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {ifConsult ? index + 1 : row.lineSeqNumber}
                  </td>
                )}

                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  {row.shipmentNumber}
                </td>

                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>Tienda {row.buCodeSource?.toUpperCase()}</p>
                  <p>{row.locationOriginCode?.toUpperCase()}</p>
                </td>

                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>Tienda {row.buCodeDestination?.toUpperCase()}</p>
                  <p>{row.locationDestinationCode?.toUpperCase()}</p>
                </td>

                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>{row.serviceName?.toUpperCase()}</p>
                  <p>{row?.paymentModeName?.toUpperCase()}</p>
                </td>

                {showPiece && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    <p>
                      {row.pieceNumber} / {row.totalPieces}
                    </p>
                    <p>
                      {row.heigh} x {row.width} x {row.lenght} cm
                    </p>
                  </td>
                )}

                {!showPiece && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {row.totalPieces !== row.pieceNumber && (
                      <div>
                        <p>{row.totalPieces} en manifiesto</p>
                        <p>{row.pieceNumber - row.totalPieces} faltante(s)</p>
                      </div>
                    )}

                    {row.totalPieces === row.pieceNumber && (
                      <p>{row.totalPieces}</p>
                    )}
                  </td>
                )}

                <td
                  className="px-4 py-4 text-xs text-left text-gray-700 font-semibold truncate"
                  title="Los totales de peso presentados corresponden a lo que se incluyó en el manifiesto"
                  data-te-toggle="tooltip"
                >
                  <div className="flex items-center">
                    <div className="w-3 mr-2">
                      {row.physicalWeight === row.chargedWeight &&
                        row.physicalWeight > 0 && (
                          <CurrencyDollarIcon className="h-4 w-4 text-indigo-600" />
                        )}
                    </div>
                    Balanza: {row.physicalWeight?.toFixed(2)} kg{" "}
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 mr-2">
                      {row.dimensionalWeight === row.chargedWeight &&
                        (row.dimensionalWeight ?? 0) > 0 && (
                          <CurrencyDollarIcon className="h-4 w-4 text-indigo-600" />
                        )}
                    </div>
                    Volumétrico: {row.dimensionalWeight?.toFixed(2)} kg{" "}
                  </div>
                </td>

                {showDayCreation && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {row.daysCreation}
                  </td>
                )}

                {showShipper && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    <p>{row.shipperName}</p>
                    <p>{row.shipperIdentification}</p>
                    <p>{row.shipperPhone}</p>
                  </td>
                )}

                {showConsignee && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    <p>{row.consigneeName}</p>
                    <p>{row.consigneeIdentification}</p>
                    <p>{row.consigneePhone}</p>
                  </td>
                )}

                {showDeliveryAddress && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold max-w-[20rem]">
                    {row.isRetiraOficina ? "En Tienda" : row.locationName}
                  </td>
                )}

                {showColumStatus && (
                  <td className="px-4 py-2 text-xs text-gray-700 font-semibold">
                    <div className="flex justify-center items-center space-x-1">
                      <div className="w-7 h-7">
                        {row.incidence === ManifestIncidence.NOT_VALIDATED ? (
                          <LoadingIcon size="18px" />
                        ) : row.incidence === ManifestIncidence.NO_ERROR ? (
                          <CheckCircleIcon className="text-green-600 w-7 h-7" />
                        ) : row.incidence === ManifestIncidence.WARNING ? (
                          <ExclamationTriangleIcon className="text-orange-600 w-7 h-7" />
                        ) : row.incidence === ManifestIncidence.ERROR ||
                          row.incidence === ManifestIncidence.CRITIC_ERROR ? (
                          <ExclamationCircleIcon className="text-red-600 w-7 h-7" />
                        ) : (
                          <QuestionMarkCircleIcon className="text-red-600 w-7 h-7" />
                        )}
                      </div>
                      <div className="w-7 h-7">
                        {row.incidenceWarning ? (
                          <ExclamationTriangleIcon className="text-orange-600 w-7 h-7" />
                        ) : row.incidenceWarning === undefined ? (
                          <LoadingIcon size="18px" />
                        ) : (
                          <div className="w-7 h-7 invisible"></div>
                        )}
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManifestDetailsTable;
